import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Rocket, Video, MapPin, Users, Building2, UserRound, Square, ChevronRight, Lock } from 'lucide-react';
import { intervisieApi } from '../../services/intervisieApi';
import { dashboardApi, DASHBOARD_KEY, TODO_KEY, type AgendaItem } from '../../services/professionalDashboardApi';
import { useOpmaak } from './opmaak';

const ICOON: Record<AgendaItem['soort'], React.ComponentType<{ className?: string }>> = {
  medewerker: UserRound,
  intervisie: Users,
  b2b: Building2,
  kennismaking: Video,
  blokkade: Lock,
  todo: Square,
};

/** Klok die elke 30 s tikt: de instapknop van intervisie gaat open zonder herladen. */
function useNu() {
  const [nu, setNu] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNu(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return nu;
}

/** Venster 1: chronologische dagagenda; intervisie springt een kwartier voor aanvang naar boven. */
export const VandaagLijst: React.FC<{ items: AgendaItem[] }> = ({ items }) => {
  const { t } = useTranslation('professionalDashboard');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const opmaak = useOpmaak();
  const nu = useNu();

  const instappen = useMutation({
    mutationFn: (sessieId: string) => intervisieApi.joinTeamsSession(sessieId),
    onSuccess: ({ join_url }) => window.open(join_url, '_blank', 'noopener,noreferrer'),
    onError: () => toast.error(t('agenda.instappenFout')),
  });

  const afvinken = useMutation({
    mutationFn: (todoId: string) => dashboardApi.wijzigTodo(todoId, { voltooid: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      queryClient.invalidateQueries({ queryKey: TODO_KEY });
    },
  });

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const Icon = ICOON[item.soort];
        const entiteitId = item.id.slice(item.id.indexOf(':') + 1);
        const bezig = item.start && item.eind && nu >= new Date(item.start).getTime() && nu <= new Date(item.eind).getTime();
        const instapOpen = item.deelnemen_vanaf && nu >= new Date(item.deelnemen_vanaf).getTime();

        if (item.soort === 'todo') {
          return (
            <li key={item.id} className="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-canvas px-3 py-2">
              <button
                type="button"
                onClick={() => afvinken.mutate(entiteitId)}
                disabled={afvinken.isPending}
                className="w-5 h-5 rounded border-2 border-navy-300 hover:border-green-600 hover:bg-green-50 flex-shrink-0"
                aria-label={t('archief.voltooid')}
              />
              <span className="flex-1 min-w-0">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-navy-400">{t('agenda.soort.todo')}</span>
                <span className="block text-sm text-navy-800 truncate">{item.titel}</span>
              </span>
            </li>
          );
        }

        return (
          <li
            key={item.id}
            className={`rounded-lg border bg-white px-3 py-2.5 ${
              item.prioriteit ? 'border-gold-400 ring-2 ring-gold-100' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="w-14 flex-shrink-0 text-right">
                <p className="text-sm font-bold text-navy-900 tabular-nums">{item.start ? opmaak.tijd(item.start) : ''}</p>
                <p className="text-[11px] text-navy-400 tabular-nums">{item.eind ? opmaak.tijd(item.eind) : ''}</p>
              </div>
              <button type="button" onClick={() => navigate(item.link)} className="flex-1 min-w-0 text-left group">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-navy-400">
                  <Icon className="w-3.5 h-3.5" />
                  {t(`agenda.soort.${item.soort}`)}
                  {bezig && <span className="ml-1 rounded-full bg-green-100 px-1.5 text-green-800 normal-case tracking-normal">{t('agenda.nu')}</span>}
                </span>
                <span className="block text-sm font-semibold text-navy-900 truncate group-hover:underline">
                  {item.soort === 'medewerker'
                    ? [item.casus ? t('agenda.casus', { casus: item.casus }) : null, item.titel ? t(`agenda.sessie.${item.titel}`, { defaultValue: item.titel }) : null]
                        .filter(Boolean)
                        .join(' · ')
                    : [item.organisatie, item.titel].filter(Boolean).join(' · ') || t(`agenda.soort.${item.soort}`)}
                </span>
                {(item.locatie_type || item.locatie) && (
                  <span className="flex items-center gap-1 text-xs text-navy-500 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {[item.locatie_type ? t(`locatie.${item.locatie_type}`, { defaultValue: item.locatie_type }) : null, item.locatie].filter(Boolean).join(' — ')}
                  </span>
                )}
              </button>
              <ChevronRight className="w-4 h-4 text-navy-300 mt-1 flex-shrink-0" />
            </div>

            {item.soort === 'intervisie' && item.deelnemen_vanaf && (
              <button
                type="button"
                disabled={!instapOpen || instappen.isPending}
                onClick={() => instappen.mutate(entiteitId)}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-navy-900 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:bg-gray-200 disabled:text-navy-400"
              >
                <Rocket className="w-4 h-4" />
                {instapOpen ? t('agenda.instappen') : t('agenda.instappenVanaf', { tijd: opmaak.tijd(item.deelnemen_vanaf) })}
              </button>
            )}
            {item.video_url && (
              <a
                href={item.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 hover:text-navy-900"
              >
                <Video className="w-3.5 h-3.5" />
                {t('agenda.video')}
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
};
